import type { SessionService } from './index';
import { computed, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { RootInstance } from 'src/store';

/**
 * The token a backend should see, and whose identity it belongs to. A
 * superuser masquerade wins while active, exactly as the axios interceptor
 * and the worker session relay resolve their effective token.
 */
export interface EffectiveSession {
    readonly accessToken: ReadonlySignal<string | null>;
    /**
     * Changes when the user a backend would see changes: starting or ending a
     * masquerade, or logging in as someone else. A token refresh for the same
     * user keeps the identity, so consumers can re-authenticate on identity
     * changes without churning on every refresh.
     */
    readonly identity: ReadonlySignal<string | null>;
}

interface MasqueradeSnapshot {
    readonly token: string | null;
    readonly userId: string | null;
}

function readMasquerade(store: Pick<RootInstance, 'getState'>): MasqueradeSnapshot {
    const auth = store.getState().auth;
    return { token: auth?.masqAccessToken?.token ?? null, userId: auth?.masqUser?.id ?? null };
}

export function createEffectiveSession(
    store: Pick<RootInstance, 'getState' | 'subscribeSelector'>,
    session: Pick<SessionService, 'accessToken' | 'state'>,
): EffectiveSession & Disposable {
    const masquerade = signal<MasqueradeSnapshot>(readMasquerade(store));
    const sync = (): void => {
        const next = readMasquerade(store);
        const current = masquerade.peek();
        if (next.token !== current.token || next.userId !== current.userId) masquerade.value = next;
    };
    const unsubscribe = store.subscribeSelector((state) => state.auth).handle(sync);

    const accessToken = computed(() => masquerade.value.token ?? session.accessToken.value);
    const identity = computed(() => {
        const masq = masquerade.value;
        if (masq.token !== null) return `masquerade:${masq.userId ?? 'pending'}`;
        const state = session.state.value;
        if (state.phase === 'authenticated' || state.phase === 'refreshing') {
            return typeof state.claims.sub === 'string' ? state.claims.sub : 'self';
        }
        return session.accessToken.value === null ? null : 'self';
    });

    return {
        accessToken,
        identity,
        [Symbol.dispose]: unsubscribe,
    };
}
